import { alpha, type Theme } from "@mui/material/styles";

import { isDarkMode } from "@/src/theme/helpers";

import { RegistrationNumberType } from "@/src/components/atoms/RegistrationNumber/types";

interface RegistrationPlateFace {
  bgColor: string;
  fgColor: string;
}

interface RegistrationPlateFrame {
  borderColor: string;
  boxShadow: string;
}

type RegistrationPlateFaces = Record<
  RegistrationNumberType,
  RegistrationPlateFace
>;

const getLightPlateFaces = (theme: Theme): RegistrationPlateFaces => ({
  standard: {
    bgColor: theme.palette.background.paper,
    fgColor: theme.palette.common.black
  },
  workVehicle: {
    bgColor: theme.palette.misc.ratingActive,
    fgColor: theme.palette.common.black
  },
  diplomatic: {
    bgColor: theme.palette.semantic.info.main,
    fgColor: theme.palette.common.white
  },
  museumRegistered: {
    bgColor: theme.palette.common.black,
    fgColor: theme.palette.common.white
  },
  testDrive: {
    bgColor: theme.palette.misc.ratingActive,
    fgColor: theme.palette.common.black
  },
  temporary: {
    bgColor: theme.palette.background.paper,
    fgColor: theme.palette.semantic.error.main
  }
});

/**
 * Dark mode keeps every plate face dark and moves the plate type onto the text
 * color. A white face would glare on dark surfaces, and the light mode faces
 * pair their color with black text, which is unreadable once inverted.
 */
const getDarkPlateFaces = (theme: Theme): RegistrationPlateFaces => {
  const face = theme.palette.misc.elevation4;

  return {
    standard: { bgColor: face, fgColor: theme.palette.text.primary },
    workVehicle: { bgColor: face, fgColor: theme.palette.misc.ratingActive },
    diplomatic: { bgColor: face, fgColor: theme.palette.semantic.info.main },
    museumRegistered: {
      bgColor: theme.palette.background.default,
      fgColor: theme.palette.text.primary
    },
    testDrive: { bgColor: face, fgColor: theme.palette.misc.ratingActive },
    temporary: { bgColor: face, fgColor: theme.palette.semantic.error.main }
  };
};

export const getRegistrationColors = (
  theme: Theme,
  type: RegistrationNumberType
): RegistrationPlateFace => {
  const faces = isDarkMode(theme)
    ? getDarkPlateFaces(theme)
    : getLightPlateFaces(theme);

  return faces[type] ?? faces.standard;
};

/**
 * The light mode drop shadow is black and disappears on dark backgrounds, so
 * dark mode outlines the plate with a dim white glow instead.
 */
export const getRegistrationFrame = (theme: Theme): RegistrationPlateFrame => {
  if (isDarkMode(theme)) {
    return {
      borderColor: theme.palette.misc.outlineBorder,
      boxShadow: `0 0 7px ${alpha(theme.palette.common.white, 0.08)}`
    };
  }

  return {
    borderColor: theme.palette.text.primary,
    boxShadow: `0 0 7px ${alpha(theme.palette.common.black, 0.25)}`
  };
};
